import { type AdminSelf, isApiError } from '@saay/api-client';
import { formatCount, formatToman, statusLabel, toPersianDigits } from '@saay/formatting';
import {
  AccordionPanel,
  Banner,
  Button,
  ChipRow,
  Combobox,
  CommandError,
  CommandFeedback,
  Dialog,
  EntityPicker,
  type EntityPickerOption,
  ErrorState,
  FileImportField,
  type FileImportPreview,
  FilterChip,
  LinearProgress,
  LoadMore,
  Ltr,
  MoneyField,
  NativeSelect,
  SegmentedButtons,
  SheetSection,
  Skeleton,
  StatusChip,
  Table,
  useToast,
} from '../kit.js';
import { type CommandState, outcomeError, useCommand } from '@saay/web-app';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router';

import type {
  AdminBuyerSummary,
  CreditProgram,
  TargetingFilter,
  TargetingPreview,
  TargetingPreviewRequest,
  TargetingRun,
  TargetingRunMember,
  TargetingRunRequest,
  TargetingType,
} from '../api.js';
import { parseBuyerIdentifierLines } from '../buyer-identifier-import.js';
import { buyerSearchFromText } from '../buyer-search.js';
import type { BuyerIdentifierResolveResult } from '../api.js';
import { AdminCommandFeedback, TimeRemaining } from '../components/admin-feedback.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { useSubmitBlocked } from '../components/form.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { GeographyFields } from '../components/pickers.js';
import { ReasonField } from '../components/reason-field.js';
import { onSheetOpenChange, useCloseSheet } from '../components/route-sheet.js';
import {
  SnapshotFacts,
  TargetingImpactMeter,
  targetingImpactHeadline,
} from '../components/targeting-snapshot.js';
import { readTargetingAudienceType, storeTargetingAudienceType } from '../device-prefs.js';
import { approvalSubmitLabel, canUse, executesApprovalDirectly } from '../permissions.js';
import {
  programPath,
  useActiveOrganizationRoles,
  useActiveOrganizations,
  useBuyers,
  useCreditProgram,
  useCreditPrograms,
  useTargetingRun,
} from '../queries.js';
import { REASON_MAX } from '../validation.js';
import {
  type CreditProgramsOutletContext,
  programTitle,
  useRefreshPrograms,
} from './credit-programs.js';

/**
 * The targeting sheet (AC §18.9.1 C7, C8; FTD §9.6): choose an ACTIVE program, one of the four
 * filters, the amount and the reason; the server's preview is the mandatory impact report, and the
 * run sends its single-use token.
 */

const MAX_INDIVIDUALS = 5000;
const PREVIEW_DEBOUNCE_MS = 650;

type RecipientBuyer = Pick<
  AdminBuyerSummary,
  'buyerId' | 'firstNameFa' | 'lastNameFa' | 'mobileMasked' | 'nationalCodeMasked'
>;

interface Recipient {
  readonly amountIrr: number | null;
  readonly buyer: RecipientBuyer;
}

interface Draft {
  readonly amountPerRecipientIrr: number | null;
  readonly cityId: string;
  readonly organizationId: string;
  readonly organizationRoleId: string;
  readonly provinceId: string;
  readonly reason: string;
  readonly recipients: readonly Recipient[];
  readonly type: TargetingType;
}

/** The request filter: exactly the fields of the chosen type (AC §18.9.1). */
export function toTargetingFilter(draft: Draft): TargetingFilter | string {
  switch (draft.type) {
    case 'INDIVIDUAL':
      if (draft.recipients.length === 0) return 'دست‌کم یک خریدار را انتخاب کنید.';
      return {
        recipients: draft.recipients.map((item) => ({
          buyerId: item.buyer.buyerId,
          ...(item.amountIrr === null ? {} : { amountIrr: item.amountIrr }),
        })),
        type: 'INDIVIDUAL',
      };
    case 'ORGANIZATION':
      if (draft.organizationId === '') return 'سازمان را انتخاب کنید.';
      return {
        organizationId: draft.organizationId,
        organizationRoleId: draft.organizationRoleId === '' ? null : draft.organizationRoleId,
        type: 'ORGANIZATION',
      };
    case 'ORGANIZATION_ROLE':
      if (draft.organizationRoleId === '') return 'نقش را انتخاب کنید.';
      return { organizationRoleId: draft.organizationRoleId, type: 'ORGANIZATION_ROLE' };
    case 'GEOGRAPHY':
      if (draft.provinceId === '') return 'استان را انتخاب کنید.';
      return {
        cityId: draft.cityId === '' ? null : draft.cityId,
        provinceId: draft.provinceId,
        type: 'GEOGRAPHY',
      };
    default:
      return 'نوع مخاطب را انتخاب کنید.';
  }
}

function normalizedDraft(draft: Draft, program: CreditProgram): Draft {
  if (program.dedicatedOrganization !== null && draft.type === 'ORGANIZATION') {
    return { ...draft, organizationId: program.dedicatedOrganization.id };
  }
  return draft;
}

export function buildTargetingPreviewRequest(
  draft: Draft,
  program: CreditProgram,
): TargetingPreviewRequest | undefined {
  const target = toTargetingFilter(normalizedDraft(draft, program));
  if (typeof target === 'string') {
    return undefined;
  }
  const reason = draft.reason.trim();
  if (reason === '' || [...reason].length > REASON_MAX) {
    return undefined;
  }
  if (draft.amountPerRecipientIrr !== null && draft.amountPerRecipientIrr <= 0) {
    // Empty means the program default; zero is never a grant.
    return undefined;
  }
  return {
    reason,
    target,
    ...(draft.amountPerRecipientIrr === null
      ? {}
      : { amountPerRecipientIrr: draft.amountPerRecipientIrr }),
  };
}

function targetingRunSubmitLabel(
  snapshot: TargetingPreview['snapshot'],
  approvalRequired: boolean,
  self: AdminSelf,
): string {
  if (approvalRequired && !executesApprovalDirectly(self)) {
    return approvalSubmitLabel(self);
  }
  return `دادن ${formatToman(snapshot.amountPerRecipientIrr)} به ${formatCount(snapshot.recipientCount)} نفر`;
}

/** The recovery line of a refused preview or run, by error code only (AC §18.9.1, §13). */
function targetingGuidance(error: unknown, draft?: Draft): string | undefined {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'TARGET_TOO_LARGE':
      return `بیش از ${toPersianDigits(typeof details.maxRecipients === 'number' ? details.maxRecipients : 5000)} دریافت‌کننده است؛ با نقش یا شهر دامنه را کوچک کنید.`;
    case 'TARGET_EMPTY':
      return draft?.organizationRoleId !== undefined &&
        draft.organizationRoleId !== '' &&
        draft.type === 'ORGANIZATION'
        ? 'پس از کنارگذاشتن موارد فاقد شرایط، دریافت‌کننده‌ای نماند؛ «همه نقش‌ها» یا سازمان دیگری را امتحان کنید.'
        : 'پس از کنارگذاشتن موارد فاقد شرایط، دریافت‌کننده‌ای نماند.';
    case 'APPROVAL_SELF_DECISION_FORBIDDEN':
      // Q-S3-05, §19.2.2: a manager never grants credit to its own buyer profile.
      return details.reason === 'OWN_TARGET'
        ? 'شما خودتان در میان دریافت‌کنندگان هستید؛ مدیر دیگری باید این تخصیص را اجرا کند، یا خودتان را از مخاطبان کنار بگذارید.'
        : 'مدیر دیگری باید این تخصیص را اجرا کند.';
    case 'APPROVAL_ROLE_NOT_ALLOWED':
      return 'مدیری با نقش مجاز (مدیر ارشد یا مدیر مالی) باید این تخصیص را اجرا کند.';
    case 'APPROVAL_REQUEST_PENDING':
      return 'درخواست تأیید دیگری برای همین کار در جریان است؛ اول آن را تعیین تکلیف کنید.';
    case 'IDEMPOTENCY_REQUEST_IN_PROGRESS':
      return 'درخواست قبلی هنوز در حال پردازش است؛ کمی بعد وضعیت را بررسی کنید.';
    case 'CREDIT_PROGRAM_NOT_ACTIVE':
      return 'برنامه فعال نیست یا بازهٔ آن به پایان رسیده است.';
    case 'CREDIT_PROGRAM_TARGET_NOT_ALLOWED':
      return 'برنامهٔ اختصاصی فقط سازمان خود یا اعضای فردی آن را هدف می‌گیرد.';
    case 'CREDIT_PROGRAM_EXPOSURE_CAP_EXCEEDED':
      return 'این اجرا از سقف کل تعهد برنامه عبور می‌کند.';
    case 'GEOGRAPHY_MISMATCH':
      return 'شهر انتخاب‌شده متعلق به این استان نیست.';
    case 'PREVIEW_EXPIRED':
      return 'مهلت پیش‌نمایش گذشته است؛ دوباره بررسی کنید.';
    case 'PREVIEW_STALE':
      return 'شرایط پس از پیش‌نمایش تغییر کرده است؛ دوباره بررسی کنید.';
    case 'STATE_CONFLICT':
      return 'سازمان انتخاب‌شده غیرفعال است.';
    default:
      return undefined;
  }
}

const TARGETING_RUN_FAILURE_FA: Readonly<Record<string, string>> = {
  CREDIT_PROGRAM_EXPOSURE_CAP_EXCEEDED: 'این اجرا از سقف کل تعهد برنامه عبور می‌کند.',
  CREDIT_PROGRAM_NOT_ACTIVE: 'برنامه فعال نیست یا بازهٔ آن به پایان رسیده است.',
};

function targetingRunFailureMessage(code: string): string {
  return TARGETING_RUN_FAILURE_FA[code] ?? statusLabel('targetingExclusionCode', code);
}

function audienceTypeOptions(program: CreditProgram): readonly {
  key: TargetingType;
  label: string;
}[] {
  const dedicated = program.dedicatedOrganization;
  return [
    { key: 'ORGANIZATION', label: 'سازمان' },
    ...(dedicated === null
      ? [
          { key: 'ORGANIZATION_ROLE' as const, label: 'نقش' },
          { key: 'GEOGRAPHY' as const, label: 'منطقه' },
        ]
      : []),
    { key: 'INDIVIDUAL', label: 'افراد' },
  ];
}

function IndividualRecipients({
  amountPerRecipientIrr,
  defaultAmountIrr,
  draft,
  set,
}: {
  readonly amountPerRecipientIrr: number | null;
  readonly defaultAmountIrr: number;
  readonly draft: Draft;
  readonly set: (changes: Partial<Draft>) => void;
}) {
  const [query, setQuery] = useState('');
  const [importPreview, setImportPreview] = useState<FileImportPreview | null>(null);
  const [unresolved, setUnresolved] = useState<BuyerIdentifierResolveResult['unresolved']>([]);
  const resolveCommand = useCommand<
    { mobileE164s?: string[]; nationalCodes?: string[] },
    BuyerIdentifierResolveResult
  >('buyer-identifiers.resolve', (client, body) =>
    client.postWithoutKey('/admin/buyer-identifiers/resolve', { body }),
  );
  const parsed = useMemo(() => buyerSearchFromText(query), [query]);
  const buyerFilters = 'filters' in parsed ? parsed.filters : {};
  const buyerMessage = 'message' in parsed ? parsed.message : undefined;
  const searchReady =
    query.trim() !== '' && 'filters' in parsed && Object.keys(buyerFilters).length > 0;
  const buyers = useBuyers(buyerFilters, searchReady);
  const rows = buyers.data?.pages.flatMap((page) => page.items) ?? [];
  const options: EntityPickerOption[] = useMemo(
    () =>
      rows
        .filter((buyer) => !draft.recipients.some((item) => item.buyer.buyerId === buyer.buyerId))
        .map((buyer) => ({
          description: buyer.mobileMasked,
          id: buyer.buyerId,
          label: `${buyer.firstNameFa} ${buyer.lastNameFa}`,
          maskedValue: buyer.nationalCodeMasked,
        })),
    [draft.recipients, rows],
  );

  function perPersonAmount(item: Recipient): number {
    return item.amountIrr ?? amountPerRecipientIrr ?? defaultAmountIrr;
  }

  async function ingestIdentifiers(text: string) {
    const parsed = parseBuyerIdentifierLines(text);
    if (parsed.mobileE164s.length === 0 && parsed.nationalCodes.length === 0) {
      setImportPreview({
        columns: ['ورودی'],
        invalidCount: 1,
        rows: [],
        validCount: 0,
      });
      return;
    }
    const outcome = await resolveCommand.submit(parsed);
    if (outcome.status !== 'success') {
      return;
    }
    const result = outcome.result.data;
    setUnresolved(result.unresolved);
    setImportPreview({
      columns: ['نام', 'ورودی'],
      invalidCount: result.unresolved.length,
      rows: result.resolved.map((row: BuyerIdentifierResolveResult['resolved'][number]) => ({
        cells: [row.displayNameFa, row.input],
        key: row.buyerId,
        status: 'valid' as const,
        statusLabel: 'پیدا شد',
      })),
      validCount: result.resolved.length,
    });
    const known = new Set(draft.recipients.map((item) => item.buyer.buyerId));
    const added: Recipient[] = [];
    for (const row of result.resolved) {
      if (known.has(row.buyerId) || draft.recipients.length + added.length >= MAX_INDIVIDUALS) {
        continue;
      }
      known.add(row.buyerId);
      added.push({
        amountIrr: null,
        buyer: {
          buyerId: row.buyerId,
          firstNameFa: row.displayNameFa.split(' ')[0] ?? row.displayNameFa,
          lastNameFa: row.displayNameFa.split(' ').slice(1).join(' ') || '—',
          mobileMasked: row.mobileMasked,
          nationalCodeMasked: row.nationalCodeMasked,
        },
      });
    }
    if (added.length > 0) {
      set({ recipients: [...draft.recipients, ...added] });
    }
  }

  return (
    <div className="admin-form">
      <FileImportField
        accept=".csv,.tsv,.txt,text/csv,text/plain"
        label="فهرست کد ملی یا موبایل را بچسبانید یا فایلش را انتخاب کنید"
        onFile={(file) => void file.text().then(ingestIdentifiers)}
        onPaste={(text) => void ingestIdentifiers(text)}
        preview={importPreview}
      />
      {unresolved.length === 0 ? null : (
        <Banner tone="warning">
          {`${formatCount(unresolved.length)} مورد پیدا نشد`}
          <ul className="admin-plain-list">
            {unresolved.slice(0, 5).map((row) => (
              <li key={row.input}>
                <Ltr>{row.input}</Ltr>
              </li>
            ))}
          </ul>
        </Banner>
      )}
      <EntityPicker
        emptyMessage={buyerMessage ?? 'نتیجه‌ای نیست.'}
        label="افراد"
        loading={buyers.isFetching}
        onQueryChange={setQuery}
        onSelect={(buyerId) => {
          if (buyerId === null || draft.recipients.length >= MAX_INDIVIDUALS) {
            return;
          }
          const buyer = rows.find((row) => row.buyerId === buyerId);
          if (buyer === undefined) {
            return;
          }
          set({ recipients: [...draft.recipients, { amountIrr: null, buyer }] });
          setQuery('');
        }}
        options={options}
        query={query}
        selectedId={null}
      />
      {draft.recipients.length === 0 ? null : (
        <ChipRow>
          {draft.recipients.map((item) => (
            <FilterChip
              key={item.buyer.buyerId}
              onRemove={() =>
                set({
                  recipients: draft.recipients.filter(
                    (other) => other.buyer.buyerId !== item.buyer.buyerId,
                  ),
                })
              }
            >
              {`${item.buyer.firstNameFa} ${item.buyer.lastNameFa} · ${formatToman(perPersonAmount(item))}`}
            </FilterChip>
          ))}
        </ChipRow>
      )}
    </div>
  );
}

function AudienceFields({
  draft,
  program,
  set,
}: {
  readonly draft: Draft;
  readonly program: CreditProgram;
  readonly set: (changes: Partial<Draft>) => void;
}) {
  const organizations = useActiveOrganizations();
  const roles = useActiveOrganizationRoles();
  const dedicated = program.dedicatedOrganization;
  const roleOptions = (roles.data ?? []).map((role) => ({
    label: `${role.nameFa} · ${role.roleCode}`,
    value: role.id,
  }));
  const orgOptions = (organizations.data ?? []).map((item) => ({
    disabled: item.activeMemberCount === 0,
    label: `${item.displayNameFa} · ${formatCount(item.activeMemberCount)} عضو فعال`,
    value: item.id,
  }));
  const organizationValue = dedicated?.id ?? draft.organizationId;
  const organizationSelectedLabel = useMemo(() => {
    if (organizationValue === '') {
      return undefined;
    }
    if (dedicated !== null) {
      return dedicated.displayNameFa;
    }
    return orgOptions.find((item) => item.value === organizationValue)?.label;
  }, [dedicated, orgOptions, organizationValue]);
  const typeOptions = audienceTypeOptions(program);

  return (
    <>
      <SegmentedButtons
        label="مخاطب"
        onChange={(key) => {
          const type = key as TargetingType;
          storeTargetingAudienceType(type);
          set({ type });
        }}
        options={typeOptions.map((option) => ({ key: option.key, label: option.label }))}
        value={draft.type}
      />
      {draft.type === 'ORGANIZATION' ? (
        <div className="admin-target-org">
          <Combobox
            disabled={dedicated !== null}
            label="سازمان"
            loading={organizations.isPending && organizationValue !== ''}
            onChange={(organizationId) => set({ organizationId })}
            options={orgOptions}
            {...(() => {
              const selectedLabel =
                organizationSelectedLabel ??
                (organizations.isPending && organizationValue !== '' ? '' : undefined);
              return selectedLabel === undefined ? {} : { selectedLabel };
            })()}
            value={organizationValue === '' ? undefined : organizationValue}
          />
          <Combobox
            label="نقش (اختیاری)"
            onChange={(organizationRoleId) => set({ organizationRoleId: organizationRoleId ?? '' })}
            options={roleOptions}
            placeholder="همه نقش‌ها"
            value={draft.organizationRoleId === '' ? undefined : draft.organizationRoleId}
          />
        </div>
      ) : null}
      {draft.type === 'ORGANIZATION_ROLE' ? (
        <Combobox
          label="نقش"
          onChange={(organizationRoleId) => set({ organizationRoleId: organizationRoleId ?? '' })}
          options={roleOptions}
          placeholder="انتخاب نقش"
          value={draft.organizationRoleId === '' ? undefined : draft.organizationRoleId}
        />
      ) : null}
      {draft.type === 'GEOGRAPHY' ? (
        <GeographyFields
          cityId={draft.cityId}
          idPrefix="targeting"
          onCityChange={(cityId) => set({ cityId })}
          onProvinceChange={(provinceId) => set({ cityId: '', provinceId })}
          provinceId={draft.provinceId}
        />
      ) : null}
      {draft.type === 'INDIVIDUAL' ? (
        <IndividualRecipients
          amountPerRecipientIrr={draft.amountPerRecipientIrr}
          defaultAmountIrr={program.defaultAmountIrr}
          draft={draft}
          set={set}
        />
      ) : null}
    </>
  );
}

/** A run refusal that the same preview can never pass: the run stays disabled until an edit. */
const RUN_BLOCKING_CODES: ReadonlySet<string> = new Set([
  'APPROVAL_REQUEST_PENDING',
  'APPROVAL_ROLE_NOT_ALLOWED',
  'APPROVAL_SELF_DECISION_FORBIDDEN',
  'CREDIT_PROGRAM_EXPOSURE_CAP_EXCEEDED',
  'CREDIT_PROGRAM_NOT_ACTIVE',
]);

/** A run refusal that spends the preview: only «بررسی دوباره مخاطبان» helps (AC §7.3). */
const PREVIEW_SPENT_CODES: ReadonlySet<string> = new Set(['PREVIEW_EXPIRED', 'PREVIEW_STALE']);

function fieldErrorFa(error: unknown, field: string): string | undefined {
  if (!isApiError(error)) return undefined;
  return error.fieldErrors.find((item) => item.field === field)?.messageFa;
}

/**
 * The outcome of «دادن …» (C8), shown inline over the actions (owner report: a refused run showed
 * nothing). A business refusal shows the server `messageFa` with its recovery line; a technical
 * error or an unknown outcome follows D-76 («بررسی وضعیت» with the same key).
 */
function TargetingRunFeedback({
  onCheckStatus,
  onRecheck,
  state,
}: {
  readonly onCheckStatus: () => void;
  readonly onRecheck: () => void;
  readonly state: CommandState<TargetingRun>;
}) {
  if (state.status === 'approval-pending') {
    return (
      <CommandFeedback
        approvalAction={
          state.approval === undefined ? undefined : (
            <Link to={`/approvals/${state.approval.approvalRequestId}`}>مشاهدهٔ درخواست تأیید</Link>
          )
        }
        onCheckStatus={onCheckStatus}
        state={state}
      />
    );
  }
  if (state.status === 'idle' || state.status === 'pending' || state.status === 'success') {
    return null;
  }
  if (state.status !== 'error' || !isApiError(state.error)) {
    return <AdminCommandFeedback onCheckStatus={onCheckStatus} state={state} />;
  }
  const error = state.error;
  const line = targetingGuidance(error);
  let action: ReactNode = null;
  if (PREVIEW_SPENT_CODES.has(error.code)) {
    action = (
      <Button onClick={onRecheck} variant="secondary">
        بررسی دوباره مخاطبان
      </Button>
    );
  } else if (error.code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS') {
    action = (
      <Button onClick={onCheckStatus} variant="secondary">
        بررسی وضعیت
      </Button>
    );
  } else if (
    error.code === 'APPROVAL_REQUEST_PENDING' &&
    typeof error.details?.approvalRequestId === 'string'
  ) {
    action = (
      <Link to={`/approvals/${error.details.approvalRequestId}`}>مشاهدهٔ درخواست تأیید</Link>
    );
  }
  if (line === undefined && action === null) {
    return <AdminCommandFeedback onCheckStatus={onCheckStatus} state={state} />;
  }
  return (
    <CommandError
      action={
        <>
          {line === undefined ? null : <p>{line}</p>}
          {action}
        </>
      }
      error={error}
    />
  );
}

function TargetingBuilder({
  initialOrganizationId,
  initialReason,
  onStickyFooter,
  program,
  self,
}: {
  readonly initialOrganizationId: string;
  readonly initialReason: string;
  readonly onStickyFooter: (footer: ReactNode) => void;
  readonly program: CreditProgram;
  readonly self: AdminSelf;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const direct = executesApprovalDirectly(self);
  const refresh = useRefreshPrograms(program.id);
  const offline = useSubmitBlocked();
  const remembered = readTargetingAudienceType();
  const initialType =
    program.dedicatedOrganization !== null && remembered === 'ORGANIZATION_ROLE'
      ? 'ORGANIZATION'
      : (remembered ?? 'ORGANIZATION');
  const [draft, setDraft] = useState<Draft>({
    amountPerRecipientIrr: null,
    cityId: '',
    organizationId: program.dedicatedOrganization?.id ?? initialOrganizationId,
    organizationRoleId: '',
    provinceId: '',
    reason: initialReason,
    recipients: [],
    type: initialType,
  });
  const [amountInvalid, setAmountInvalid] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [preview, setPreview] = useState<TargetingPreview | null>(null);
  const [expired, setExpired] = useState(false);
  const runResetRef = useRef<() => void>(() => undefined);
  const previewCommand = useCommand<TargetingPreviewRequest, TargetingPreview>(
    'credit-program.targeting.preview',
    (client, body) =>
      client.postWithoutKey(programPath(program.id, '/targeting-previews'), { body }),
    {
      onSuccess: (result) => {
        setPreview(result.data);
        setExpired(false);
        // A fresh preview replaces the refusal of the previous run attempt.
        runResetRef.current();
      },
    },
  );
  const runCommand = useCommand<TargetingRunRequest, TargetingRun>(
    'credit-program.targeting.run',
    (client, body, intent) =>
      client.post(programPath(program.id, '/targeting-runs'), { body, intent }),
    {
      onApprovalPending: async () => {
        setPreview(null);
        await refresh();
      },
      onSuccess: async (result) => {
        await refresh();
        setPreview(null);
        if (result.data.status === 'COMPLETED') {
          toast.success({
            confirmedBy: result,
            description: `${formatCount(result.data.recipientCount ?? 0)} نفر · ${formatToman(result.data.allocatedTotalIrr ?? 0)}`,
            title: 'تخصیص اعتبار انجام شد',
          });
        }
        // EXECUTING (D-59) and every other run state: the run page follows it.
        void navigate(`/credit-programs/${program.id}/runs/${result.data.id}`, { replace: true });
      },
    },
  );
  runResetRef.current = runCommand.reset;

  function set(changes: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...changes }));
    setPreview(null);
    previewCommand.reset();
    runCommand.reset();
  }

  const reasonText = draft.reason.trim();
  const reasonError =
    [...reasonText].length > REASON_MAX
      ? 'دلیل حداکثر ۱۰۰۰ کاراکتر است.'
      : reasonTouched && reasonText === ''
        ? 'دلیل تخصیص را بنویسید.'
        : undefined;
  const amountError =
    draft.amountPerRecipientIrr !== null && draft.amountPerRecipientIrr <= 0
      ? 'مبلغ هر نفر باید بیشتر از صفر باشد؛ برای مبلغ پیش‌فرض آن را خالی بگذارید.'
      : undefined;

  function check(): TargetingPreviewRequest | undefined {
    setReasonTouched(true);
    const target = toTargetingFilter(normalizedDraft(draft, program));
    if (typeof target === 'string') {
      setProblem(target);
      return undefined;
    }
    if (amountInvalid || amountError !== undefined) {
      setProblem('مبلغ هر نفر معتبر نیست؛ آن را درست کنید یا خالی بگذارید.');
      return undefined;
    }
    // A missing or long reason is shown on its field.
    setProblem(undefined);
    return buildTargetingPreviewRequest(draft, program);
  }

  // `useCommand` returns a fresh object on every render, so effects depend on its stable members
  // (a whole-object dependency re-ran the footer effect on every render and looped).
  const submitPreview = previewCommand.submit;
  const resetPreview = previewCommand.reset;
  const submitRun = runCommand.submit;
  const checkRunStatus = runCommand.checkStatus;
  const runState = runCommand.state;
  const runStatus = runState.status;
  const checkRef = useRef(check);
  checkRef.current = check;
  const previewRequest = useMemo(
    () => (amountInvalid ? undefined : buildTargetingPreviewRequest(draft, program)),
    [amountInvalid, draft, program],
  );

  useEffect(() => {
    if (previewRequest === undefined) {
      return undefined;
    }
    const timer = setTimeout(() => {
      void submitPreview(previewRequest);
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [submitPreview, previewRequest]);

  const snapshot = preview?.snapshot;
  const previewState = previewCommand.state;
  const previewPending = previewState.status === 'pending';
  const previewError = previewState.status === 'error' ? previewState.error : undefined;
  const previewLine =
    previewError === undefined ? undefined : targetingGuidance(previewError, draft);
  const canRun = canUse(self, 'runCreditProgramTargeting');
  const runErrorCode =
    runState.status === 'error' && isApiError(runState.error) ? runState.error.code : undefined;
  const runBlocked = runErrorCode !== undefined && RUN_BLOCKING_CODES.has(runErrorCode);
  const noRecipients = snapshot !== undefined && snapshot.recipientCount <= 0;

  useEffect(() => {
    const recheck = () => {
      const body = checkRef.current();
      if (body !== undefined) {
        resetPreview();
        void submitPreview(body);
      }
    };
    const feedback = (
      <TargetingRunFeedback
        onCheckStatus={() => void checkRunStatus()}
        onRecheck={recheck}
        state={runState}
      />
    );
    if (runStatus === 'approval-pending') {
      onStickyFooter(feedback);
      return () => onStickyFooter(null);
    }
    const retry = (
      <Button
        disabled={offline.blocked || runStatus === 'pending'}
        loading={previewPending}
        onClick={recheck}
        // Before any preview this is the step to take: «بررسی مخاطبان», the filled action (W4: it
        // said «دوباره» the first time).
        variant={preview === null ? 'primary' : 'secondary'}
      >
        {preview === null ? 'بررسی مخاطبان' : 'بررسی دوباره مخاطبان'}
      </Button>
    );
    onStickyFooter(
      <div className="admin-target-footer">
        {feedback}
        <div className="admin-actions">
          {retry}
          {preview === null || !canRun ? null : (
            <Button
              disabled={
                offline.blocked ||
                preview.snapshot.capExceeded ||
                preview.snapshot.recipientCount <= 0 ||
                runBlocked
              }
              loading={runStatus === 'pending'}
              onClick={() => {
                void submitRun({
                  previewToken: preview.previewToken,
                  reason: draft.reason.trim(),
                  snapshotHash: preview.snapshotHash,
                }).then((outcome) => {
                  const error = outcomeError(outcome);
                  if (isApiError(error) && PREVIEW_SPENT_CODES.has(error.code)) {
                    // The token is spent or stale: drop the report; the next step is a new preview.
                    setPreview(null);
                  }
                });
              }}
            >
              {targetingRunSubmitLabel(preview.snapshot, preview.approvalRequired === true, self)}
            </Button>
          )}
        </div>
      </div>,
    );
    return () => onStickyFooter(null);
  }, [
    canRun,
    checkRunStatus,
    draft.reason,
    offline.blocked,
    onStickyFooter,
    preview,
    previewPending,
    resetPreview,
    runBlocked,
    runState,
    runStatus,
    submitPreview,
    submitRun,
    self,
  ]);

  return (
    <div className="admin-target-layout">
      <div className="admin-form admin-target-form">
        <p className="admin-muted">
          {`${programTitle(program)} · هر نفر ${formatToman(program.defaultAmountIrr)}`}
        </p>
        <AudienceFields draft={draft} program={program} set={set} />
        <MoneyField
          error={amountError ?? fieldErrorFa(previewError, 'amountPerRecipientIrr')}
          hint={`خالی یعنی مبلغ پیش‌فرض برنامه (${formatToman(program.defaultAmountIrr)})`}
          label="مبلغ هر نفر"
          onChangeRials={(amountPerRecipientIrr) => set({ amountPerRecipientIrr })}
          onInvalidChange={setAmountInvalid}
          valueRials={draft.amountPerRecipientIrr}
        />
        <ReasonField
          error={reasonError ?? fieldErrorFa(previewError, 'reason')}
          intent="credit.targeting"
          onBlur={() => setReasonTouched(true)}
          onChange={(reason) => set({ reason })}
          value={draft.reason}
        />
        {problem === undefined ? null : <Banner tone="warning">{problem}</Banner>}
      </div>
      <section
        aria-labelledby="targeting-impact-title"
        aria-live="polite"
        className="admin-target-review"
      >
        <div className="admin-target-review__header">
          <span className="saay-eyebrow" id="targeting-impact-title">
            گزارش اثر
          </span>
          {snapshot === undefined ? null : noRecipients || snapshot.capExceeded ? (
            <StatusChip title="قابل اجرا نیست" tone="danger" />
          ) : preview?.approvalRequired === true && !direct ? (
            <StatusChip title="نیازمند تأیید دوم" tone="warning" />
          ) : (
            <StatusChip title="قابل اجرا" tone="success" />
          )}
        </div>
        {snapshot === undefined ? (
          previewPending || previewState.status !== 'idle' ? null : (
            <p className="admin-muted">
              مخاطب و دلیل را بنویسید؛ تعداد دریافت‌کنندگان و اعتبار موردنیاز اینجا نمایش داده
              می‌شود.
            </p>
          )
        ) : (
          <>
            <p className="admin-target-impact__headline">{targetingImpactHeadline(snapshot)}</p>
            <TargetingImpactMeter snapshot={snapshot} />
            {noRecipients ? (
              <Banner tone="warning">
                پس از کنارگذاشتن موارد فاقد شرایط، دریافت‌کننده‌ای نماند؛ مخاطب را تغییر دهید.
              </Banner>
            ) : null}
            <TimeRemaining
              expiredLabel="مهلت پیش‌نمایش گذشت؛ دوباره بررسی کنید."
              expiresAt={preview!.expiresAt}
              label="مهلت پیش‌نمایش"
              onExpire={() => {
                setPreview(null);
                setExpired(true);
              }}
            />
            <SnapshotFacts snapshot={snapshot} />
            {snapshot.recipientsSample.length === 0 ? null : (
              <AccordionPanel
                items={[
                  {
                    content: (
                      <ul className="admin-plain-list">
                        {snapshot.recipientsSample.map((item) => (
                          <li key={item.buyerId}>
                            {`${item.displayNameFa} · ${formatToman(item.amountIrr)}`}
                          </li>
                        ))}
                      </ul>
                    ),
                    key: 'sample',
                    title: `نمونه (${formatCount(snapshot.recipientsSample.length)} نفر)`,
                  },
                ]}
                label="نمونه دریافت‌کنندگان"
              />
            )}
          </>
        )}
        {expired ? (
          <Banner tone="warning">زمان پیش‌نمایش تمام شد؛ دوباره بررسی کنید.</Banner>
        ) : null}
        {previewPending ? <LinearProgress label="در حال بررسی مخاطبان…" /> : null}
        {previewState.status === 'unknown' ||
        (previewError !== undefined && previewLine === undefined) ? (
          <AdminCommandFeedback
            onCheckStatus={() => void previewCommand.checkStatus()}
            state={previewState}
          />
        ) : null}
        {previewError === undefined || previewLine === undefined ? null : (
          <CommandError action={<p>{previewLine}</p>} error={previewError} />
        )}
      </section>
    </div>
  );
}

/** `/credit-programs/targeting` (pick an ACTIVE program) and `/credit-programs/:programId/targeting`. */
export function TargetingSheet() {
  const { self } = useOutletContext<CreditProgramsOutletContext>();
  const { programId: routeProgramId } = useParams();
  const [params] = useSearchParams();
  const [picked, setPicked] = useState(params.get('programId') ?? '');
  const [stickyFooter, setStickyFooter] = useState<ReactNode>(null);
  const programId = routeProgramId ?? picked;
  const close = useCloseSheet(
    routeProgramId === undefined ? '/credit-programs' : `/credit-programs/${routeProgramId}`,
  );
  const active = useCreditPrograms({ status: 'ACTIVE' }, routeProgramId === undefined);
  const program = useCreditProgram(programId, programId !== '');
  usePageTitle('تخصیص جدید');
  const activeRows = active.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <Dialog
      eyebrow="تخصیص جدید"
      footer={stickyFooter}
      // Phones get the whole screen (MD3 full-screen dialog); the form and its report are long.
      fullScreenOnCompact
      onOpenChange={onSheetOpenChange(close)}
      open
      // Desktop: the form and the sticky impact report side by side (.admin-target-layout).
      size="wizard"
      stickyFooter={stickyFooter !== null}
      title="چه کسانی این اعتبار را بگیرند؟"
    >
      {!canUse(self, 'previewCreditProgramTargeting') ? (
        <Banner tone="warning">هدف‌گذاری فقط برای مدیر ارشد و مدیر مالی ممکن است.</Banner>
      ) : (
        <>
          {routeProgramId === undefined ? (
            <NativeSelect
              hint={
                active.status === 'pending'
                  ? 'در حال بارگذاری برنامه‌های فعال…'
                  : activeRows.length === 0
                    ? 'برنامهٔ فعالی نیست؛ اول یک برنامه را فعال کنید.'
                    : undefined
              }
              label="برنامهٔ فعال"
              onChange={(event) => setPicked(event.target.value)}
              options={activeRows.map((item) => ({
                label: `${programTitle(item)} · هر نفر ${formatToman(item.defaultAmountIrr)}`,
                value: item.id,
              }))}
              placeholder="انتخاب برنامه"
              value={picked}
            />
          ) : null}
          {programId === '' ? null : (
            <QueryState
              error={program.error}
              onRetry={() => void program.refetch()}
              skeleton={<Skeleton label="در حال بارگذاری برنامه…" lines={4} />}
              status={program.status}
            >
              {program.data === undefined ? null : program.data.status !== 'ACTIVE' ? (
                <Banner tone="warning">
                  {`این برنامه ${statusLabel('creditProgramStatus', program.data.status)} است؛ فقط برنامهٔ فعال هدف‌گذاری می‌شود.`}
                </Banner>
              ) : (
                <TargetingBuilder
                  initialOrganizationId={params.get('organizationId') ?? ''}
                  initialReason={params.get('reason') ?? ''}
                  key={program.data.id}
                  onStickyFooter={setStickyFooter}
                  program={program.data}
                  self={self}
                />
              )}
            </QueryState>
          )}
        </>
      )}
    </Dialog>
  );
}

type MemberFilter = 'ALLOCATED' | 'ALL' | 'EXCLUDED';

function runResultLine(run: TargetingRun): string | null {
  if (run.recipientCount === null) {
    return null;
  }
  const excluded = run.excludedCount ?? 0;
  const total = formatToman(run.allocatedTotalIrr ?? 0);
  return `${formatCount(run.recipientCount)} نفر · ${total} · ${formatCount(excluded)} کنار گذاشته`;
}

function TargetingRunProgress({
  progress,
}: {
  readonly progress: NonNullable<TargetingRun['progress']>;
}) {
  const { candidateCount, processedCandidateCount } = progress;
  const label = `در حال اجرا: ${formatCount(processedCandidateCount)} از ${formatCount(candidateCount)} خریدار`;
  const percent =
    candidateCount <= 0
      ? 0
      : Math.min(100, Math.max(0, (processedCandidateCount / candidateCount) * 100));
  return (
    <section aria-live="polite" className="admin-target-review" role="status">
      <LinearProgress label={label} value={percent} />
    </section>
  );
}

function filterRunMembers(
  members: readonly TargetingRunMember[],
  filter: MemberFilter,
): readonly TargetingRunMember[] {
  if (filter === 'ALL') {
    return members;
  }
  if (filter === 'ALLOCATED') {
    return members.filter((row) => row.resolutionStatus === 'ALLOCATED');
  }
  return members.filter((row) => row.resolutionStatus !== 'ALLOCATED');
}

/** `/credit-programs/:programId/runs/:runId`. */
export function TargetingRunSheet() {
  const { programId = '', runId = '' } = useParams();
  const run = useTargetingRun(programId, runId);
  const close = useCloseSheet(`/credit-programs/${programId}`);
  const [memberFilter, setMemberFilter] = useState<MemberFilter>('ALL');
  usePageTitle('جزئیات هدف‌گذاری');
  const first = run.data?.pages[0];
  const members = run.data?.pages.flatMap((page) => page.members.items) ?? [];
  const filteredMembers = filterRunMembers(members, memberFilter);
  const organizations = useActiveOrganizations(first !== undefined);
  const runTarget = first?.snapshot.target;
  const orgLabel =
    runTarget?.type === 'ORGANIZATION'
      ? (organizations.data ?? []).find((row) => row.id === runTarget.organizationId)?.displayNameFa
      : undefined;
  const orgPending =
    runTarget?.type === 'ORGANIZATION' && organizations.isPending && orgLabel === undefined;
  const title =
    first === undefined
      ? 'جزئیات هدف‌گذاری'
      : orgPending
        ? `تخصیص ${first.snapshot.programCode}`
        : orgLabel === undefined
          ? `تخصیص ${first.snapshot.programCode}`
          : `تخصیص ${first.snapshot.programCode} · ${orgLabel}`;
  const resultLine = first === undefined ? null : runResultLine(first);

  return (
    <Dialog
      // A run is named by its program and audience; a cut UUID («0199a1b2») told nobody anything.
      eyebrow="اجرای هدف‌گذاری"
      onOpenChange={onSheetOpenChange(close)}
      open
      size="wide"
      title={title}
    >
      <QueryState
        error={run.error}
        onRetry={() => void run.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری اجرا…" lines={8} />}
        status={run.isFetchNextPageError ? 'success' : run.status}
      >
        {first === undefined ? null : (
          <>
            <div className="admin-inline">
              <StatusChip enumName="targetingRunStatus" value={first.status} />
              {resultLine === null ? null : <strong>{resultLine}</strong>}
            </div>
            {first.status === 'EXECUTING' && first.progress !== null ? (
              <TargetingRunProgress progress={first.progress} />
            ) : null}
            {first.status === 'FAILED' && first.failureCode !== null ? (
              <Banner tone="danger">
                {`اجرا ناموفق بود: ${targetingRunFailureMessage(first.failureCode)}`}
              </Banner>
            ) : null}
            {first.status === 'PENDING_APPROVAL' && first.approvalRequestId !== null ? (
              <Banner tone="info">
                {'این اجرا در انتظار تأیید است. '}
                <Link to={`/approvals/${first.approvalRequestId}`}>مشاهدهٔ درخواست</Link>
              </Banner>
            ) : null}
            <AccordionPanel
              items={[
                {
                  content: <SnapshotFacts snapshot={first.snapshot} />,
                  key: 'snapshot',
                  summary: targetingImpactHeadline(first.snapshot),
                  title: 'گزارش اثر تأییدشده',
                },
              ]}
              label="گزارش اثر تأییدشده"
            />
            {first.status === 'EXECUTING' ? null : (
              <SheetSection id="run-members" title="اعضای حل‌شده">
                <FilterPillBar
                  activeFilterCount={memberFilter === 'ALL' ? 0 : 1}
                  label="فیلتر اعضای اجرای هدف‌گذاری"
                  onClearAll={() => setMemberFilter('ALL')}
                >
                  <FilterPill
                    emptyLabel="همه نتیجه‌ها"
                    label="نتیجه"
                    onChange={(value) =>
                      setMemberFilter(value === '' ? 'ALL' : (value as MemberFilter))
                    }
                    options={[
                      { label: 'تخصیص‌یافته', value: 'ALLOCATED' },
                      { label: 'کنار گذاشته', value: 'EXCLUDED' },
                    ]}
                    value={memberFilter === 'ALL' ? '' : memberFilter}
                  />
                </FilterPillBar>
                <Table
                  caption="اعضای اجرای هدف‌گذاری"
                  columns={[
                    {
                      header: 'خریدار',
                      key: 'name',
                      pin: 'start',
                      render: (row) => (
                        <Link to={`/buyers/${row.buyerId}`}>{row.displayNameFa}</Link>
                      ),
                    },
                    {
                      header: 'نتیجه',
                      key: 'result',
                      render: (row) =>
                        row.resolutionStatus === 'ALLOCATED'
                          ? 'تخصیص‌یافته'
                          : statusLabel('targetingExclusionCode', row.exclusionCode ?? ''),
                    },
                    {
                      header: 'مبلغ',
                      key: 'amount',
                      numeric: true,
                      pin: 'end',
                      render: (row) =>
                        row.resolvedAmountIrr === null ? '—' : formatToman(row.resolvedAmountIrr),
                    },
                  ]}
                  rowKey={(row) => row.buyerId}
                  rows={filteredMembers}
                />
                {run.isFetchNextPageError ? (
                  <ErrorState error={run.error} onRetry={() => void run.fetchNextPage()} />
                ) : null}
                <LoadMore
                  hasMore={run.hasNextPage}
                  loading={run.isFetchingNextPage}
                  onLoadMore={() => void run.fetchNextPage()}
                />
              </SheetSection>
            )}
          </>
        )}
      </QueryState>
    </Dialog>
  );
}
